// Shared mutable store bridging the globe's three.js scene out to plain DOM
// hover code: DatacenterScreenPositionsPublisher (inside the canvas) writes
// every visible datacenter's canvas-local screen position each frame AND
// registers a synchronous per-pixel hit test, and the HUD's globe hover
// (hud/blocks/globe/globeHoverSwap.tsx) calls the hit test on mousemove to
// decide whether the pointer is on the opaque globe and which datacenter it
// is on. No pointer events on the canvas, no react state churn per frame.
// Same pattern as DcInkOutlineStore / the band-HUD overlay channel's
// "publish, don't measure".
//
// A module singleton (not context): the publisher lives inside the r3f canvas
// where outside React contexts don't reach, and only one globe mounts a
// publisher at a time (the HUD's).

// A datacenter counts as hovered while the pointer is within this many px of
// its projected center on the globe — the generous fallback when the ray
// doesn't hit the site's stack directly (small sites are only a few px
// tall).
export const GLOBE_DC_HOVER_RADIUS_PX = 16

export type DatacenterScreenPosition = {
  name: string
  // Canvas-local CSS pixels (origin = canvas top-left).
  x: number
  y: number
  compute: number
}

export type GlobeHitTestResult = {
  // The pointer is on an opaque pixel of the globe: the central sphere or a
  // datacenter stack sticking out past its silhouette. False over the
  // transparent canvas around the earth (the graph's risk dither ring shows
  // through there and keeps its own hover).
  overOpaqueGlobe: boolean
  // The datacenter under the pointer: the stack the ray hits directly, else
  // the nearest projected center within GLOBE_DC_HOVER_RADIUS_PX. Satellite
  // stacks resolve to their parent site's name (the name the HUD's list
  // rows carry).
  hoveredDcName: string | null
}

// How the EARTH itself responds while the globe hover is engaged (the HUD's
// datacenter-list takeover showing) — one HUD tab each; the scene components
// read these from the store per frame:
//   "rim-thicker"        — the sphere outline grows by extraWidthPx (keeps
//                          the variant's own rim color)
//   "rim-black"          — the rim recolors to full black at its stock width
//   "rim-accent-thicker" — the rim recolors to the accent red AND grows
//   "continents-bold"    — the coastline linework thickens
//   "stacks-red"         — every datacenter stack fills the accent red
export type GlobeEarthHoverTreatment =
  | "rim-thicker"
  | "rim-black"
  | "rim-accent-thicker"
  | "continents-bold"
  | "stacks-red"

// Knobs for the earth hover treatments. WebGL can't read CSS variables, so
// the accent red is the literal value of --accent (#8B0000).
export const GLOBE_EARTH_HOVER_STYLE = {
  rimThicker: { extraWidthPx: 1 },
  rimBlack: { color: "#111111" },
  rimAccentThicker: { color: "#8B0000", extraWidthPx: 1 },
  continentsBold: { lineWidthMultiplier: 1.8 },
  stacksRed: { color: "#8B0000" },
  // Exponential ease factor for the treatments' fade in/out (the datacenter
  // stacks' own ANIM_SPEED voice — higher = snappier).
  transitionSpeedPerSec: 8,
}

export type DatacenterScreenPositionsStore = {
  positions: DatacenterScreenPosition[]
  // EVERY site on the drawn hemisphere (facing the camera at all), by the
  // parent-resolved name the list rows carry. Looser than `positions` (which
  // culls near-limb sites for pointer-fallback precision): the raycast can
  // still name a limb site by hitting its stack directly, so the list's
  // visible-first ordering must include them or their hover lands on
  // "And N more".
  visibleSiteNames: Set<string>
  // Sites buried under a pile-mate's taller stack at the same hex cell
  // (published by HexDatacenterLayer from the placement pass): unreadable on
  // the globe, so they're excluded from visibleSiteNames and positions —
  // the list only shows them if ordinary size rank earns them a row.
  buriedSiteNames: Set<string>
  // The globe's canvas element, for mapping client coords to canvas-local.
  canvasEl: HTMLCanvasElement | null
  // Synchronous raycast against the live scene (sphere + datacenter layers),
  // in viewport client coordinates. Registered by
  // DatacenterScreenPositionsPublisher while a globe scene is mounted.
  hitTest: ((clientX: number, clientY: number) => GlobeHitTestResult) | null
  // True while the pointer is on the opaque globe (the datacenter-list
  // takeover is showing) — the scene's earth hover treatments key off it.
  earthHovered: boolean
  // Which earth hover treatment the active HUD tab wants; null = none.
  earthHoverTreatment: GlobeEarthHoverTreatment | null
  // True while the HUD's globe hover feature owns the globe: the old
  // floating datacenter tooltips stay closed (the list takeover replaces
  // them).
  datacenterTooltipsSuppressed: boolean
}

export const datacenterScreenPositionsStore: DatacenterScreenPositionsStore = {
  positions: [],
  visibleSiteNames: new Set(),
  buriedSiteNames: new Set(),
  canvasEl: null,
  hitTest: null,
  earthHovered: false,
  earthHoverTreatment: null,
  datacenterTooltipsSuppressed: false,
}
